import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { useQuery, useQueryClient } from 'react-query'
import { SearchContext } from '../../context/SearchContext'
import { api } from '../../services/api'

export enum TransactionType {
  ENTRADA = 'ENTRADA',
  SAIDA = 'SAIDA',
  AJUSTE = 'AJUSTE',
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('pt-BR')
}

const fetchStockTransactions = async () => {
  const response = await api.get('/stock-transactions')
  return response.data.data
}

const fetchRawMaterials = async () => {
  const response = await api.get('/raw-materials')
  return response.data
}

export default function StockManagement() {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: transactions, isLoading: loadingTransactions } = useQuery('stockTransactions', fetchStockTransactions)

  const { data: rawMaterials, isLoading: loadingRawMaterials } = useQuery('rawMaterials', fetchRawMaterials)

  const [selectedTab, setSelectedTab] = useState(0)
  const transactionsArray = Array.isArray(transactions) ? transactions : []
  const [selectedProductId] = useState<number | null>(null)

  // Removido: 'currentPage' e 'itemsPerPage' genéricos
  // const [currentPage, setCurrentPage] = useState(1)
  // const itemsPerPage = 10

  const { isOpen: isRegisterModalOpen, onOpen: onOpenRegisterModal, onClose: onCloseRegisterModal } = useDisclosure()
  const { isOpen: isProductModalOpen, onOpen: onOpenProductModal, onClose: onCloseProductModal } = useDisclosure()
  const { isOpen: isReportModalOpen, onOpen: onOpenReportModal, onClose: onCloseReportModal } = useDisclosure()

  const [selectedTransaction, setSelectedTransaction] = useState<any>(null)
  const [modalActiveOnly, setModalActiveOnly] = useState(true)
  const [stockActiveOnly, setStockActiveOnly] = useState(true)
  const uniqueProducts = rawMaterials || []
  const { searchValue } = useContext(SearchContext)

  const productsForTransactionSelect = useMemo(() => {
    const list = modalActiveOnly ? uniqueProducts.filter((p: any) => p.is_enable) : uniqueProducts
    return [...list].sort((a: any, b: any) =>
      (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' }),
    )
  }, [uniqueProducts, modalActiveOnly])

  // Removido: 'filteredProducts', 'totalPages', 'currentProducts' (eram do modal de busca removido)

  const filteredTransactions = useMemo(() => {
    let txList = selectedProductId
      ? transactionsArray.filter((tx: any) => tx.rawMaterial?.id === selectedProductId)
      : transactionsArray
    if ((searchValue || '').trim() !== '') {
      txList = txList.filter((tx: any) =>
        (tx.rawMaterial?.name || uniqueProducts.find((prod: any) => prod.id === tx.raw_material_id)?.name || '')
          .toLowerCase()
          .includes((searchValue || '').toLowerCase()),
      )
    }
    return txList
  }, [transactionsArray, selectedProductId, searchValue, uniqueProducts])

  // Lógica de Paginação para Transações
  const [currentPageTransactions, setCurrentPageTransactions] = useState(1)
  const itemsPerPageTransactions = 10 // Ou o número desejado por página
  const totalPagesTransactions = Math.ceil(filteredTransactions.length / itemsPerPageTransactions)
  const currentTransactions = useMemo(() => {
    const start = (currentPageTransactions - 1) * itemsPerPageTransactions
    const end = start + itemsPerPageTransactions
    return filteredTransactions.slice(start, end)
  }, [filteredTransactions, currentPageTransactions, itemsPerPageTransactions])

  const filteredMaterials = useMemo(() => {
    return uniqueProducts
      .filter((mat: any) => (stockActiveOnly ? mat.is_enable : !mat.is_enable))
      .filter((mat: any) => mat.name.toLowerCase().includes((searchValue || '').toLowerCase()))
  }, [uniqueProducts, searchValue, stockActiveOnly])

  const handleRegistrar = () => {
    setModalActiveOnly(true)
    setTransactionForm({
      raw_material_id: 0,
      quantity: '', // Quantidade atual no estoque
      quantity_to_add: '', // Quantidade a adicionar (para ENTRADA)
      quantity_to_remove: '', // Quantidade a remover (para SAIDA)
      new_quantity: '', // Nova quantidade (para AJUSTE)
      notes: '',
      expiration_date: '',
      transaction_type: TransactionType.ENTRADA,
    })
    onOpenRegisterModal()
  }

  const handleOpenProductDetails = (tx: any) => {
    setSelectedTransaction(tx)
    onOpenProductModal()
  }

  // Função para atualizar a quantidade atual quando um produto for selecionado
  const handleProductSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productId = Number(e.target.value)
    const selectedProduct = uniqueProducts.find((prod: any) => prod.id === productId)

    setTransactionForm({
      ...transactionForm,
      raw_material_id: productId,
      quantity: selectedProduct ? selectedProduct.amount || 0 : '',
    })
  }

  const [transactionForm, setTransactionForm] = useState({
    raw_material_id: 0,
    quantity: '', // Quantidade atual no estoque
    quantity_to_add: '', // Quantidade a adicionar (para ENTRADA)
    quantity_to_remove: '', // Quantidade a remover (para SAIDA)
    new_quantity: '', // Nova quantidade (para AJUSTE)
    notes: '',
    expiration_date: '',
    transaction_type: TransactionType.ENTRADA,
  })

  useEffect(() => {
    if (!transactionForm.raw_material_id) return
    const stillVisible = productsForTransactionSelect.some((p: any) => p.id === transactionForm.raw_material_id)
    if (!stillVisible) {
      setTransactionForm((prev) => ({ ...prev, raw_material_id: 0, quantity: '' }))
    }
  }, [modalActiveOnly, productsForTransactionSelect, transactionForm.raw_material_id])

  const [reportForm, setReportForm] = useState({
    start_date: '',
    end_date: '',
  })

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = e.target
    let { value } = e.target

    if (name === 'new_quantity') {
      value = value.replace(/[^0-9.]/g, '')
      const parts = value.split('.')
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('')
      }
    }

    // Se for mudança de produto, atualiza a quantidade atual
    if (name === 'raw_material_id') {
      const productId = Number(value)
      const selectedProduct = uniqueProducts.find((prod: any) => prod.id === productId)
      console.log('Produto selecionado:', selectedProduct) // Debug
      setTransactionForm({
        ...transactionForm,
        [name]: productId,
        quantity: selectedProduct ? selectedProduct.amount || 0 : '',
      })
      return
    }

    setTransactionForm({
      ...transactionForm,
      [name]: value,
    })
  }

  const handleSubmitTransaction = async () => {
    if (transactionForm.transaction_type === TransactionType.ENTRADA) {
      // Para ENTRADA, valida o campo quantity_to_add
      if (
        transactionForm.quantity_to_add === '' ||
        isNaN(Number(transactionForm.quantity_to_add)) ||
        Number(transactionForm.quantity_to_add) <= 0
      ) {
        toast({
          description: 'O campo Quantidade a Adicionar é obrigatório e deve ser um número maior que zero.',
          status: 'error',
        })
        return
      }
    } else if (transactionForm.transaction_type === TransactionType.AJUSTE) {
      // Para AJUSTE, valida o campo new_quantity
      if (transactionForm.new_quantity === '' || isNaN(Number(transactionForm.new_quantity))) {
        toast({
          description: 'O campo Nova Quantidade é obrigatório e deve ser um número.',
          status: 'error',
        })
        return
      }
    } else if (transactionForm.transaction_type === TransactionType.SAIDA) {
      // Para SAIDA, valida o campo quantity_to_remove
      if (
        transactionForm.quantity_to_remove === '' ||
        isNaN(Number(transactionForm.quantity_to_remove)) ||
        Number(transactionForm.quantity_to_remove) <= 0
      ) {
        toast({
          description: 'O campo Quantidade a Remover é obrigatório e deve ser um número maior que zero.',
          status: 'error',
        })
        return
      }
    }

    const validatedForm = {
      ...transactionForm,
      expiration_date: transactionForm.expiration_date === '' ? null : transactionForm.expiration_date,
      quantity:
        transactionForm.transaction_type === TransactionType.ENTRADA
          ? Number(transactionForm.quantity_to_add)
          : transactionForm.transaction_type === TransactionType.SAIDA
          ? Number(transactionForm.quantity_to_remove)
          : 0,
      new_quantity:
        transactionForm.transaction_type === TransactionType.AJUSTE ? Number(transactionForm.new_quantity) : null,
    }

    console.log('Formulário enviado:', validatedForm) // Debug

    try {
      const response = await api.post('/stock-transactions/registrarTransacao', validatedForm)
      if (response.status === 201) {
        toast({
          description: 'Transação registrada com sucesso',
          status: 'success',
        })
        queryClient.invalidateQueries('stockTransactions')
        queryClient.invalidateQueries('rawMaterials')
        onCloseRegisterModal()
      } else {
        toast({
          description: 'Falha ao registrar a transação',
          status: 'error',
        })
      }
    } catch (error) {
      console.error(error)
      toast({ description: 'Erro ao registrar a transação', status: 'error' })
    }
  }

  const [currentPageMaterial, setCurrentPageMaterial] = useState(1)
  const itemsPerPageMaterials = 8
  const totalPagesMaterials = Math.ceil(filteredMaterials.length / itemsPerPageMaterials)
  const currentMaterials = useMemo(() => {
    const start = (currentPageMaterial - 1) * itemsPerPageMaterials
    return filteredMaterials.slice(start, start + itemsPerPageMaterials)
  }, [filteredMaterials, currentPageMaterial])

  const handleAjustar = (mat: any) => {
    setTransactionForm({
      ...transactionForm,
      raw_material_id: mat.id,
      transaction_type: TransactionType.AJUSTE,
      quantity: mat.amount || 0,
      quantity_to_add: '',
      quantity_to_remove: '',
      new_quantity: '',
    })
    onOpenRegisterModal()
  }

  const handleReportFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setReportForm({
      ...reportForm,
      [name]: value,
    })
  }

  const handleGenerateReport = async () => {
    if (!reportForm.start_date || !reportForm.end_date) {
      toast({
        description: 'Por favor, selecione as datas inicial e final.',
        status: 'error',
      })
      return
    }

    try {
      const response = await api.get('/stock-transactions/product-report', {
        params: {
          start_date: reportForm.start_date,
          end_date: reportForm.end_date,
        },
        responseType: 'blob', // Importante para download de arquivos
      })

      // Criar um link para download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `relatorio_estoque_${reportForm.start_date}_${reportForm.end_date}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast({
        description: 'Relatório gerado com sucesso!',
        status: 'success',
      })
      onCloseReportModal()
    } catch (error) {
      console.error(error)
      toast({
        description: 'Erro ao gerar o relatório',
        status: 'error',
      })
    }
  }

  useEffect(() => {
    setCurrentPageTransactions(1)
  }, [searchValue])

  useEffect(() => {
    setCurrentPageMaterial(1)
  }, [searchValue, stockActiveOnly])

  return (
    <Box p="8">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb="6">
        <Heading>Gerenciador de estoque</Heading>
        <Box display="flex" gap="3">
          <Button onClick={onOpenReportModal} colorScheme="blue">
            Gerar Relatório
          </Button>
          <Button onClick={handleRegistrar} colorScheme="green">
            Registrar Nova Transação
          </Button>
        </Box>
      </Box>
      <Tabs index={selectedTab} onChange={setSelectedTab}>
        <TabList>
          <Tab>Transações de Estoque</Tab>
          <Tab>Estoque</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {loadingTransactions ? (
              <Spinner />
            ) : (
              <>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>ID</Th>
                      <Th>Produto</Th>
                      <Th>Tipo</Th>
                      <Th>Data</Th>
                      <Th>Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {/* Alterado para 'currentTransactions' */}
                    {currentTransactions.map((tx: any) => (
                      <Tr key={tx.id}>
                        <Td>{tx.id}</Td>
                        <Td>
                          {tx.rawMaterial?.name ||
                            uniqueProducts.find((prod: any) => prod.id === tx.raw_material_id)?.name ||
                            'N/A'}
                        </Td>
                        <Td>{tx.transaction_type}</Td>
                        <Td>{formatDate(tx.created_at)}</Td>
                        <Td>
                          <Button size="xs" mr="2" onClick={() => handleOpenProductDetails(tx)} colorScheme="pink">
                            <FaSearch />
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {/* Controles de Paginação para Transações */}
                <Box mt="3" display="flex" justifyContent="space-between">
                  <Button
                    size="xs"
                    colorScheme="pink"
                    onClick={() => setCurrentPageTransactions((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPageTransactions === 1}>
                    Anterior
                  </Button>
                  <Box as="span" fontSize="sm" alignSelf="center">
                    {currentPageTransactions} de {totalPagesTransactions}
                  </Box>
                  <Button
                    size="xs"
                    colorScheme="pink"
                    onClick={() => setCurrentPageTransactions((prev) => Math.min(prev + 1, totalPagesTransactions))}
                    disabled={currentPageTransactions === totalPagesTransactions}>
                    Próximo
                  </Button>
                </Box>
              </>
            )}
          </TabPanel>
          <TabPanel>
            {loadingRawMaterials ? (
              <Spinner />
            ) : (
              <>
                <Flex mb="4" align="center" gap="3">
                  <Switch
                    id="stock-active-filter"
                    colorScheme="green"
                    isChecked={stockActiveOnly}
                    onChange={(e) => setStockActiveOnly(e.target.checked)}
                  />
                  <Text fontSize="sm" fontWeight="medium">
                    {stockActiveOnly ? 'Somente ativos' : 'Somente inativos'}
                  </Text>
                </Flex>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Material</Th>
                      <Th>Quantidade Atual</Th>
                      <Th>Estoque Mínimo</Th>
                      <Th>Estoque Máximo</Th>
                      <Th>Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentMaterials.map((mat: any) => (
                      <Tr key={mat.id}>
                        <Td>{mat.name}</Td>
                        <Td>{mat.amount}</Td>
                        <Td>{mat.inventory_min}</Td>
                        <Td>{mat.inventory_max}</Td>
                        <Td>
                          <Button size="xs" onClick={() => handleAjustar(mat)} colorScheme="purple">
                            Ajustar
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                <Box mt="3" display="flex" justifyContent="space-between">
                  <Button
                    size="xs"
                    colorScheme="pink"
                    onClick={() => setCurrentPageMaterial((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPageMaterial === 1}>
                    Anterior
                  </Button>
                  <Box as="span" fontSize="sm" alignSelf="center">
                    {currentPageMaterial} de {totalPagesMaterials}
                  </Box>
                  <Button
                    size="xs"
                    colorScheme="pink"
                    onClick={() => setCurrentPageMaterial((prev) => Math.min(prev + 1, totalPagesMaterials))}
                    disabled={currentPageMaterial === totalPagesMaterials}>
                    Próximo
                  </Button>
                </Box>
              </>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={isRegisterModalOpen} onClose={onCloseRegisterModal}>
        <ModalOverlay />
        <ModalContent bg="gray.900">
          <ModalHeader>Registrar Nova Transação</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb="3">
              <Flex mb="2" align="center" gap="3">
                <Switch
                  id="modal-active-filter"
                  colorScheme="green"
                  isChecked={modalActiveOnly}
                  onChange={(e) => setModalActiveOnly(e.target.checked)}
                />
                <Text fontSize="sm" fontWeight="medium">
                  Somente ativos
                </Text>
              </Flex>
              <FormLabel>Produto</FormLabel>
              <Select
                name="raw_material_id"
                value={transactionForm.raw_material_id || ''}
                onChange={handleFormChange}
                placeholder="Selecione um produto"
                color="black" // Garante texto preto
                sx={{
                  '& option': {
                    color: 'black',
                  },
                  '&:focus': {
                    color: 'black', // Ajustado para preto no foco
                  },
                }}
                _focus={{
                  color: 'black', // Ajustado para preto no foco
                }}
                bg="white">
                {productsForTransactionSelect.map((prod: any) => (
                  <option key={prod.id} value={prod.id} style={{ color: 'black' }}>
                    {(prod.name || '').toUpperCase()} ({prod.measurement_unit})
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb="3">
              <FormLabel>Quantidade Atual</FormLabel>
              <Input
                name="current_quantity"
                placeholder="Quantidade atual no estoque"
                value={transactionForm.quantity}
                disabled={true}
              />
            </FormControl>
            {transactionForm.transaction_type === TransactionType.ENTRADA && (
              <FormControl mb="3">
                <FormLabel>Quantidade a Adicionar</FormLabel>
                <Input
                  name="quantity_to_add"
                  placeholder="Quantidade a adicionar"
                  value={transactionForm.quantity_to_add || ''}
                  onChange={handleFormChange}
                  type="number"
                  step="any"
                />
              </FormControl>
            )}
            {transactionForm.transaction_type === TransactionType.AJUSTE && (
              <FormControl mb="3">
                <FormLabel>Nova Quantidade</FormLabel>
                <Input
                  name="new_quantity"
                  placeholder="Nova Quantidade"
                  value={transactionForm.new_quantity}
                  onChange={handleFormChange}
                  type="number"
                  step="any"
                />
              </FormControl>
            )}
            {transactionForm.transaction_type === TransactionType.SAIDA && (
              <FormControl mb="3">
                <FormLabel>Quantidade a Remover</FormLabel>
                <Input
                  name="quantity_to_remove"
                  placeholder="Quantidade a remover"
                  value={transactionForm.quantity_to_remove || ''}
                  onChange={handleFormChange}
                  type="number"
                  step="any"
                />
              </FormControl>
            )}
            <FormControl mb="3">
              <FormLabel>Observações</FormLabel>
              <Input name="notes" placeholder="Observações" value={transactionForm.notes} onChange={handleFormChange} />
            </FormControl>
            <FormControl mb="3">
              <FormLabel>Data de Validade</FormLabel>
              <Input
                name="expiration_date"
                type="date"
                value={transactionForm.expiration_date}
                onChange={handleFormChange}
              />
            </FormControl>
            <FormControl mb="3">
              <FormLabel>
                Tipo de Transação{' '}
                <Tooltip
                  label={
                    <Box>
                      <div>
                        <strong>ENTRADA:</strong> Adiciona novo material ao estoque.
                      </div>
                      <div>
                        <strong>SAIDA:</strong> Remove material do estoque.
                      </div>
                      <div>
                        <strong>AJUSTE:</strong> Ajusta a quantidade disponível para corrigir inconsistências.
                      </div>
                    </Box>
                  }
                  aria-label="Explicação dos tipos de transação"
                  hasArrow
                  placement="right"
                  bg="gray.700"
                  color="white"
                  p={2}
                  maxW="300px">
                  <span
                    style={{
                      marginLeft: '5px',
                      color: '#3182ce',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      verticalAlign: 'middle',
                    }}>
                    ?
                  </span>
                </Tooltip>
              </FormLabel>
              <Select
                name="transaction_type"
                value={transactionForm.transaction_type}
                onChange={handleFormChange}
                color="black" // Garante texto preto
                sx={{
                  '& option': {
                    color: 'black',
                  },
                  '&:focus': {
                    color: 'black', // Ajustado para preto no foco
                  },
                }}
                _focus={{
                  color: 'black', // Ajustado para preto no foco
                }}
                bg="white">
                <option value={TransactionType.ENTRADA}>ENTRADA</option>
                <option value={TransactionType.SAIDA}>SAIDA</option>
                <option value={TransactionType.AJUSTE}>AJUSTE</option>
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSubmitTransaction}>
              Registrar
            </Button>
            <Button variant="ghost" onClick={onCloseRegisterModal}>
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isProductModalOpen} onClose={onCloseProductModal}>
        <ModalOverlay />
        <ModalContent bg="gray.900">
          <ModalHeader>Detalhes do Produto da Transação</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box>
              <Box mb="3">
                <strong>ID da Transação:</strong> {selectedTransaction?.id || 'N/A'}
              </Box>
              <Box mb="3">
                <strong>Nome do Produto:</strong>{' '}
                {selectedTransaction?.rawMaterial?.name ||
                  uniqueProducts.find((prod: any) => prod.id === selectedTransaction?.raw_material_id)?.name ||
                  'N/A'}
              </Box>
              <Box mb="3">
                <strong>Quantidade na Transação:</strong> {selectedTransaction?.quantity || 'N/A'}
              </Box>
              <Box mb="3">
                <strong>Nova Quantidade:</strong> {selectedTransaction?.new_quantity || 'N/A'}
              </Box>
              <Box mb="3">
                <strong>Tipo de Transação:</strong> {selectedTransaction?.transaction_type || 'N/A'}
              </Box>
              <Box mb="3">
                <strong>Data da Transação:</strong>{' '}
                {selectedTransaction ? formatDate(selectedTransaction.created_at) : 'N/A'}
              </Box>
              <Box mb="3">
                <strong>Data de Validade:</strong>{' '}
                {selectedTransaction?.expiration_date
                  ? formatDate(selectedTransaction.expiration_date)
                  : 'Não contem validade'}
              </Box>
              <Box mb="3">
                <strong>Observações:</strong> {selectedTransaction?.notes || 'N/A'}
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseProductModal}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isReportModalOpen} onClose={onCloseReportModal}>
        <ModalOverlay />
        <ModalContent bg="gray.900">
          <ModalHeader>Gerar Relatório de Estoque</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb="3">
              <FormLabel>Data Inicial</FormLabel>
              <Input name="start_date" type="date" value={reportForm.start_date} onChange={handleReportFormChange} />
            </FormControl>
            <FormControl mb="3">
              <FormLabel>Data Final</FormLabel>
              <Input name="end_date" type="date" value={reportForm.end_date} onChange={handleReportFormChange} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleGenerateReport}>
              Gerar Relatório
            </Button>
            <Button variant="ghost" onClick={onCloseReportModal}>
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
